import {
  listBusinessExclusiveFeatures,
  PLAN_POSITIONING,
} from "../../../constants/planMarketing";
import {
  formatSubscriptionRub,
  partnerDiscountedAmount,
} from "../../../utils/clubSubscriptionUi";
import { Button } from "../../ui";
import ss from "./subscription.module.css";

function planPrice(plan, period) {
  if (!plan) return 0;
  return period === "yearly"
    ? plan.yearly_price_rub || plan.monthly_price_rub * 12
    : plan.monthly_price_rub;
}

function planFeatureLines(plan) {
  const f = plan?.features || {};
  const lines = [];
  if (f.trainer_portal) lines.push("Кабинет тренера");
  if (f.client_portal) lines.push("Кабинет клиента");
  if (f.max_trainers) lines.push(`До ${f.max_trainers} тренеров`);
  if (f.max_clients) lines.push(`До ${f.max_clients} участников`);
  if (f.member_social && f.club_life) lines.push("Соцсеть и лента");
  else if (f.chat) lines.push("Чат с клиентами");
  if (f.branding) lines.push("Полное оформление");
  else if (f.branding_lite) lines.push("Логотип клуба");
  if (f.polls) lines.push("Опросы и «Приду»");
  if (plan.code === "business") {
    listBusinessExclusiveFeatures(f).slice(0, 2).forEach((item) => {
      if (!lines.includes(item)) lines.push(item);
    });
  }
  return lines.slice(0, 5);
}

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/**
 * Сумма на кнопке (как на бэкенде):
 * - тот же период → (M−D)×((B−A)/M);
 * - другой период → цена года − остаток текущего месяца.
 */
function upgradePayAmount({
  price,
  period,
  currentPeriod,
  isCurrentPlan,
  checkout,
}) {
  const full = Number(price) || 0;
  if (!checkout) return full;

  const daysLeft = Math.max(0, Number(checkout.days_left) || 0);
  const periodDays =
    Number(checkout.period_days) > 0
      ? Number(checkout.period_days)
      : currentPeriod === "yearly"
        ? 365
        : 30;
  const currentAmount = Number(checkout.current_amount_rub) || 0;
  const credit = Number(checkout.available_credit_rub || checkout.credit_rub || 0);

  if (isCurrentPlan && period === currentPeriod) {
    return full;
  }

  if (period === currentPeriod) {
    const diff = full - currentAmount;
    if (diff <= 0 || daysLeft <= 0) return 0;
    return Math.max(0, roundMoney(daysLeft * (diff / periodDays)));
  }

  return Math.max(0, roundMoney(full - credit));
}

function payActionLabel({ paying, planLocked, isCurrentPlan, isUpgrade }) {
  if (paying) return "Переход к оплате…";
  if (planLocked) return "Доступно после окончания срока";
  if (isUpgrade) return "Расширить";
  if (isCurrentPlan) return "Продлить";
  return "Оплатить";
}

function quoteMatchesPlan(checkout, planId, period) {
  if (!checkout || checkout.billing_period !== period) return false;
  if (checkout.target_saas_plan_id == null || planId == null) return false;
  return String(checkout.target_saas_plan_id) === String(planId);
}

export default function ClubSubscriptionPlanPicker({
  plans,
  period,
  onPeriodChange,
  selectedPlanId,
  onSelectPlan,
  onPay,
  payingPlanId,
  isTrial,
  canPay = true,
  currentPlanId = null,
  priceLocked = false,
  lockedMonthlyPrice = null,
  renewOnly = false,
  currentPeriod = "monthly",
  checkout = null,
  checkoutLoading = false,
  hasActivePaidPeriod = false,
}) {
  if (!canPay) {
    return (
      <p className={ss.pickerHint}>
        Оплату оформляет владелец или администратор клуба.
      </p>
    );
  }

  const daysLeft = checkout?.days_left;
  const samePeriodHint =
    hasActivePaidPeriod &&
    period === currentPeriod &&
    daysLeft != null &&
    daysLeft > 0;
  const yearHint =
    hasActivePaidPeriod &&
    period !== currentPeriod &&
    Number(checkout?.available_credit_rub || checkout?.credit_rub || 0) > 0;

  return (
    <section>
      <div className={ss.pickerHead}>
        <h4 className={ss.pickerTitle}>Тариф и оплата</h4>
      </div>

      <div className={ss.period}>
        <button
          type="button"
          className={`${ss.periodBtn} ${period === "monthly" ? ss.periodBtnOn : ""}`}
          onClick={() => onPeriodChange("monthly")}
          disabled={renewOnly && currentPeriod !== "monthly"}
        >
          Месяц
        </button>
        <button
          type="button"
          className={`${ss.periodBtn} ${period === "yearly" ? ss.periodBtnOn : ""}`}
          onClick={() => onPeriodChange("yearly")}
          disabled={renewOnly && currentPeriod !== "yearly"}
        >
          Год
        </button>
      </div>

      {renewOnly ? (
        <p className={ss.pickerHint}>
          Оплаченный период ещё активен — сейчас доступно только продление текущего
          тарифа. Смена тарифа откроется после окончания оплаченного срока.
        </p>
      ) : samePeriodHint ? (
        <p className={ss.pickerHint}>
          Осталось {daysLeft} дн. из {checkout?.period_days || "—"} — доплата
          только за разницу тарифов на эти дни (срок окончания не меняется).
        </p>
      ) : yearHint ? (
        <p className={ss.pickerHint}>
          Зачёт неиспользованных {daysLeft ?? "—"} дн. текущего тарифа: −
          {formatSubscriptionRub(
            checkout.available_credit_rub || checkout.credit_rub,
          )}{" "}
          ₽. Годовой тариф начнётся с даты оплаты.
        </p>
      ) : hasActivePaidPeriod ? (
        <p className={ss.pickerHint}>
          Можно расширить тариф на оставшиеся дни или перейти на год с зачётом
          остатка.
        </p>
      ) : null}

      {Number(checkout?.partner_discount_percent) > 0 ? (
        <p className={ss.partnerHint}>
          −{checkout.partner_discount_percent}% по партнёрской ссылке
          {Number(checkout.partner_discount_months_left) > 1
            ? ` · ещё ${checkout.partner_discount_months_left} мес.`
            : ""}
        </p>
      ) : null}

      <div className={ss.planList}>
        {plans.length === 0 ? (
          <p className={ss.pickerHint}>Не удалось загрузить тарифы.</p>
        ) : (
          plans.map((plan) => {
            const isCurrentPlan =
              currentPlanId != null && String(plan.id) === String(currentPlanId);
            const price =
              isCurrentPlan && priceLocked && lockedMonthlyPrice != null
                ? period === "yearly"
                  ? Number(lockedMonthlyPrice) * 12
                  : Number(lockedMonthlyPrice)
                : planPrice(plan, period);
            const selected = selectedPlanId === plan.id;
            const planLocked = renewOnly && !isCurrentPlan;
            const periodMismatch = renewOnly && period !== currentPeriod;
            const isDemoPlan = isTrial && plan.code === "business";
            const paying = payingPlanId === plan.id;
            const featured = Boolean(plan.badge);
            const features = planFeatureLines(plan);
            const periodLabel = period === "yearly" ? "год" : "мес";
            const periodChanged = period !== currentPeriod;
            const isUpgrade =
              !renewOnly &&
              hasActivePaidPeriod &&
              (!isCurrentPlan || periodChanged);
            const serverDeal =
              quoteMatchesPlan(checkout, plan.id, period) &&
              Number(checkout.partner_discount_percent) > 0 &&
              Number(checkout.amount_before_partner_discount_rub) >
                Number(checkout.amount_rub);
            const previewAmount =
              !serverDeal &&
              !hasActivePaidPeriod &&
              Number(checkout?.partner_discount_percent) > 0
                ? partnerDiscountedAmount(price, {
                    period,
                    monthly: period === "yearly" ? Number(price) / 12 : Number(price) || 0,
                    monthsLeft: checkout.partner_discount_months_left,
                    percent: checkout.partner_discount_percent,
                  })
                : null;
            const listAmount = serverDeal
              ? Number(checkout.amount_before_partner_discount_rub)
              : previewAmount != null && previewAmount < Number(price)
                ? Number(price)
                : null;
            const dueAmount = serverDeal
              ? Number(checkout.amount_rub)
              : previewAmount != null && previewAmount < Number(price)
                ? previewAmount
                : null;
            const payAmount = serverDeal
              ? Number(checkout.amount_rub)
              : hasActivePaidPeriod
                ? upgradePayAmount({
                    price,
                    period,
                    currentPeriod,
                    isCurrentPlan,
                    checkout,
                  })
                : dueAmount != null
                  ? dueAmount
                  : Number(price) || 0;
            const cardStrike =
              listAmount != null &&
              dueAmount != null &&
              !hasActivePaidPeriod &&
              Math.abs(listAmount - Number(price)) < 0.05;

            return (
              <article
                key={plan.id}
                className={`${ss.planCard} ${
                  selected ? ss.planCardOn : ""
                } ${featured ? ss.planCardFeatured : ""} ${
                  isDemoPlan ? ss.planCardDemo : ""
                }`}
              >
                <button
                  type="button"
                  className={ss.planSelect}
                  onClick={() => onSelectPlan(plan.id)}
                  disabled={planLocked}
                >
                  <div className={ss.planHead}>
                    <div className={ss.planNameRow}>
                      <span className={ss.planName}>{plan.name}</span>
                      {plan.badge ? (
                        <span className={ss.planTag}>{plan.badge}</span>
                      ) : null}
                      {isCurrentPlan ? (
                        <span className={ss.planTag}>текущий</span>
                      ) : null}
                      {isDemoPlan ? (
                        <span className={ss.planTag}>ваше демо</span>
                      ) : null}
                    </div>
                    <div className={ss.planPrice}>
                      {cardStrike ? (
                        <s className={ss.planPriceOld}>
                          {formatSubscriptionRub(listAmount)} ₽
                        </s>
                      ) : null}
                      <span className={cardStrike ? ss.planPriceNow : undefined}>
                        {formatSubscriptionRub(cardStrike ? dueAmount : price)} ₽
                      </span>
                      <small> / {periodLabel}</small>
                    </div>
                  </div>
                  {PLAN_POSITIONING[plan.code] ? (
                    <p className={ss.planPos}>{PLAN_POSITIONING[plan.code]}</p>
                  ) : null}
                  <ul className={ss.planFeatures}>
                    {features.map((line) => (
                      <li key={line} className={ss.planFeature}>
                        {line}
                      </li>
                    ))}
                  </ul>
                </button>
                <Button
                  type="button"
                  className={ss.planPayBtn}
                  disabled={
                    Boolean(payingPlanId) ||
                    !price ||
                    planLocked ||
                    periodMismatch ||
                    checkoutLoading ||
                    (isUpgrade && payAmount <= 0)
                  }
                  onClick={() => onPay(plan.id)}
                >
                  {payActionLabel({
                    paying,
                    planLocked,
                    isCurrentPlan,
                    isUpgrade,
                  })}
                  {!paying && !planLocked ? (
                    <>
                      {" · "}
                      {listAmount != null &&
                      dueAmount != null &&
                      payAmount < listAmount - 0.009 ? (
                        <>
                          <s className={ss.payOld}>
                            {formatSubscriptionRub(listAmount)} ₽
                          </s>{" "}
                        </>
                      ) : null}
                      {formatSubscriptionRub(payAmount)} ₽
                    </>
                  ) : null}
                </Button>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}
